import React, { useState, useEffect, useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import LoadingOverlay from '../components/LoadingOverlay';
import {
    getAdministracionesAdmin,
    saveAdministracion,
    getMenuOcultosAdministracion,
    saveMenuOcultosAdministracion,
} from '../service/AdministracionService';
import { getMenuAdminDefinition } from '../constants/menuAdminDefinition';
import { useTerminologia } from '../hooks/useTerminologia';
import ArbolMenuCheckboxes, { hojasDeMenuItem } from '../components/ArbolMenuCheckboxes';

const TIPOS_ADMINISTRACION = [
    { label: 'Barrio Cerrado', value: 'Barrio Cerrado' },
    { label: 'Edificio', value: 'Edificio' },
];

const administradoraVacia = {
    administracionId: 0,
    razonSocial: '',
    email: '',
    tipoAdministracion: 'Barrio Cerrado',
    logo: '',
    esDemo: false,
    activo: true,
};

const Administradoras = () => {
    const toast = useRef(null);
    const t = useTerminologia();
    const definicionMenu = useMemo(() => getMenuAdminDefinition(t), [t]);

    const [administradoras, setAdministradoras] = useState([]);
    const [form, setForm] = useState(administradoraVacia);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [menuOcultos, setMenuOcultos] = useState(new Set());
    const [loadingMenu, setLoadingMenu] = useState(false);
    const [guardandoMenu, setGuardandoMenu] = useState(false);

    useEffect(() => {
        cargarAdministradoras();
    }, []);

    const cargarAdministradoras = async () => {
        setLoading(true);
        try {
            const res = await getAdministracionesAdmin();
            setAdministradoras(res.data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las administradoras', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const cargarMenuOcultos = async (administracionId) => {
        setLoadingMenu(true);
        try {
            const { data } = await getMenuOcultosAdministracion(administracionId);
            setMenuOcultos(new Set(data ?? []));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los menús habilitados', life: 3000 });
            setMenuOcultos(new Set());
        } finally {
            setLoadingMenu(false);
        }
    };

    const abrirNuevo = () => {
        setForm(administradoraVacia);
        setMenuOcultos(new Set());
        setDialogVisible(true);
    };

    const abrirEditar = (a) => {
        setForm({
            administracionId: a.administracionId,
            razonSocial: a.razonSocial || '',
            email: a.email || '',
            tipoAdministracion: a.tipoAdministracion || 'Barrio Cerrado',
            logo: a.logo || '',
            esDemo: a.esDemo,
            activo: a.activo,
        });
        cargarMenuOcultos(a.administracionId);
        setDialogVisible(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const toggleMenuItem = (key) => {
        setMenuOcultos(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleMenuGrupo = (item, marcar) => {
        const claves = hojasDeMenuItem(item, []);
        setMenuOcultos(prev => {
            const next = new Set(prev);
            claves.forEach(k => marcar ? next.delete(k) : next.add(k));
            return next;
        });
    };

    const guardarMenu = async () => {
        setGuardandoMenu(true);
        try {
            await saveMenuOcultosAdministracion(form.administracionId, Array.from(menuOcultos));
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Menús guardados correctamente', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudieron guardar los menús', life: 4000 });
        } finally {
            setGuardandoMenu(false);
        }
    };

    const guardar = async () => {
        if (!form.razonSocial.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá la razón social', life: 3000 });
            return;
        }

        setSaving(true);
        try {
            await saveAdministracion(form);
            toast.current.show({ severity: 'success', summary: 'OK', detail: 'Administradora guardada correctamente', life: 3000 });
            setDialogVisible(false);
            cargarAdministradoras();
        } catch (err) {
            const msg = err.response?.data?.errorDisplay || 'Error al guardar la administradora';
            toast.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const accionesTemplate = (a) => (
        <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => abrirEditar(a)}
            tooltip="Editar"
        />
    );

    const activoTemplate = (a) => a.activo
        ? <span className="customer-badge status-ingreso">Activo</span>
        : <span className="customer-badge status-renewal" style={{ background: '#fde8e8', color: '#c0392b' }}>No Activo</span>;

    const demoTemplate = (a) => a.esDemo
        ? <span className="customer-badge status-proposal">Demo</span>
        : <span style={{ color: '#aaa' }}>—</span>;

    const footerDialog = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} disabled={saving} />
            <Button label="Guardar" icon="pi pi-check" onClick={guardar} loading={saving} />
        </div>
    );

    return (
        <>
            <h4>Administradoras</h4>
            <br />

            <Toast ref={toast} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nueva administradora" icon="pi pi-plus" onClick={abrirNuevo} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={administradoras}
                        loading={loading}
                        paginator
                        rows={10}
                        emptyMessage="No hay administradoras"
                        responsiveLayout="scroll"
                        className="bg-white"
                    >
                        <Column field="razonSocial" header="Razón Social" sortable />
                        <Column field="email" header="Email" sortable />
                        <Column field="tipoAdministracion" header="Tipo" sortable />
                        <Column header="Demo" body={demoTemplate} style={{ width: '90px' }} />
                        <Column header="Estado" body={activoTemplate} style={{ width: '100px' }} />
                        <Column header="" body={accionesTemplate} style={{ width: '60px' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog
                header={form.administracionId === 0 ? 'Nueva administradora' : 'Editar administradora'}
                visible={dialogVisible}
                style={{ width: '700px' }}
                onHide={() => setDialogVisible(false)}
                footer={footerDialog}
            >
                <div className="p-fluid flex flex-column gap-3 mt-2">
                    <div>
                        <label className="block mb-1">Razón Social *</label>
                        <InputText value={form.razonSocial} onChange={e => handleChange('razonSocial', e.target.value)} />
                    </div>

                    <div>
                        <label className="block mb-1">Email</label>
                        <InputText value={form.email} onChange={e => handleChange('email', e.target.value)} />
                    </div>

                    <div>
                        <label className="block mb-1">Tipo *</label>
                        <Dropdown
                            value={form.tipoAdministracion}
                            options={TIPOS_ADMINISTRACION}
                            onChange={e => handleChange('tipoAdministracion', e.value)}
                        />
                    </div>

                    <div>
                        <label className="block mb-1">Logo (nombre de archivo o URL)</label>
                        <InputText value={form.logo} onChange={e => handleChange('logo', e.target.value)} />
                    </div>

                    <div className="flex align-items-center gap-2 mt-1">
                        <Checkbox
                            inputId="esDemo"
                            checked={form.esDemo}
                            onChange={e => handleChange('esDemo', e.checked)}
                        />
                        <label htmlFor="esDemo">Es demo</label>
                    </div>

                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="activo"
                            checked={form.activo}
                            onChange={e => handleChange('activo', e.checked)}
                        />
                        <label htmlFor="activo">Administradora activa</label>
                    </div>

                    <hr />

                    <div>
                        <strong>Menús habilitados</strong>
                        <p style={{ color: '#667085', fontSize: '0.9rem', margin: '4px 0 12px' }}>
                            Elegí qué pantallas puede usar esta administradora. Aplica a todos sus usuarios (Administrador y Perfil Administrador).
                        </p>

                        {form.administracionId === 0 ? (
                            <p style={{ color: '#888' }}>Guardá primero los datos básicos para poder configurar los menús.</p>
                        ) : (
                            <>
                                <LoadingOverlay visible={loadingMenu} />
                                {!loadingMenu && (
                                    <>
                                        <div className="mb-3" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                            <ArbolMenuCheckboxes
                                                definicionMenu={definicionMenu}
                                                ocultos={menuOcultos}
                                                onToggleItem={toggleMenuItem}
                                                onToggleGrupo={toggleMenuGrupo}
                                            />
                                        </div>
                                        <Button label="Guardar menús" icon="pi pi-check" onClick={guardarMenu} loading={guardandoMenu} className="p-button-outlined" />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default Administradoras;
